import * as React from "react";
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
} from "@fluentui/react-components";

export const STATUS_VALUES = ["Offen", "Erledigt"] as const;
export type StatusValue = (typeof STATUS_VALUES)[number];

// Einheitliche Farbkodierung wie StatusCell/StatusBadgeLarge: Offen = Aubergine,
// Erledigt = Grün (vorher färbte dieser Pfad „Offen" rot).
const statusVisual = (status: string): { bg: string; fg: string } => {
    switch (status) {
        case "Erledigt": return { bg: "#e7f6ec", fg: "#0e7c3a" };
        default: return { bg: "#f3ecf3", fg: "#6b2d5c" };
    }
};

type Props = {
    status: string;
    /** Whether the current user may change the status (HV or admin). */
    canChange: boolean;
    onChange: (next: StatusValue) => void;
    size?: "small" | "medium";
};

/**
 * Status shown as a coloured chip that opens a dropdown to pick Offen /
 * Erledigt. When the user is not the Hauptverantwortliche/r (and
 * not admin) the chip is inert and shows an explanatory tooltip.
 */
export const StatusDropdown: React.FC<Props> = ({ status, canChange, onChange, size = "medium" }) => {
    const v = statusVisual(status);
    const pad = size === "small" ? "3px 10px" : "5px 12px";
    const fontSize = size === "small" ? 11 : 12;

    const chip = (
        <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            disabled={!canChange}
            title={canChange ? "Status ändern" : "Nur Hauptverantwortliche/r darf den Status ändern"}
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: pad,
                borderRadius: 16,
                fontSize,
                fontWeight: 600,
                whiteSpace: "nowrap",
                border: "none",
                backgroundColor: v.bg,
                color: v.fg,
                cursor: canChange ? "pointer" : "not-allowed",
                opacity: canChange ? 1 : 0.55,
                fontFamily: "inherit",
            }}
        >
            {status}
            {canChange ? <span style={{ fontSize: fontSize - 1 }}>▾</span> : null}
        </button>
    );

    if (!canChange) return chip;

    return (
        <Menu>
            <MenuTrigger disableButtonEnhancement>{chip}</MenuTrigger>
            <MenuPopover>
                <MenuList>
                    {STATUS_VALUES.map((s) => (
                        <MenuItem
                            key={s}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (s !== status) onChange(s);
                            }}
                        >
                            {s}
                        </MenuItem>
                    ))}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default StatusDropdown;
